import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { motion, useAnimate } from 'framer-motion'

import structure from '@/structure'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { useGetPoints } from '@/hooks/use-get-poi'
import useMediaQuery from '@/hooks/use-media-query'
import Skeleton from '@/components/skeleton'

import PoiList from './poi-list'
import TabBar from './tab-bar'
import { Props } from './types'

const MapComponent: any = dynamic(() => import('./map-component'), {
  ssr: false
})

const Wrapper = styled.div`
  min-height: 400px;
  max-width: ${structure.page.width}px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    min-height: 300px;
    max-width: 100%;
  }
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    gap: 24px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 12px;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const Title = styled.span`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  line-height: 32px;
  font-family: Inter;
  display: flex;
  align-items: flex-start;
  gap: 3px;
`

const TextWrapper = styled(motion.div)`
  font-family: Inter;
  font-size: ${fontSizes.regular};
  line-height: 24px;
  overflow: hidden;
`

const PointsOfInterest = ({
  buildingId,
  listing,
  isStreetNumberHidden = false,
  hideContent = false,
  hideSchools = false,
  icon = 'house',
  customBuildingColor,
  customPoiColor,
  isListingLocked = false
}: Props) => {
  const { t } = useTranslation('poi')
  const [currentTab, setCurrentTab] = useState(0)
  const [activeId, setActiveId] = useState<number | null>(null)
  const [currentPoint, setCurrentPoint] = useState<any>()
  const [schoolsReadMore, setSchoolsReadMore] = useState(false)
  const [showEllipsis, setShowEllipsis] = useState(false)
  const [inAction, setInAction] = useState(false)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const { data: points, isLoading } = useGetPoints(buildingId)

  const [scope] = useAnimate()

  const onToggleShowSchools = () => {
    setSchoolsReadMore(!schoolsReadMore)
    if (!showEllipsis) {
      setInAction(true)

      setTimeout(() => {
        setShowEllipsis(!showEllipsis)
        setInAction(false)
      }, 500)
    } else {
      setInAction(true)
      setShowEllipsis(!showEllipsis)
      setTimeout(() => {
        setInAction(false)
      }, 350)
    }
  }
  const allTabs = [
    [
      ...(points?.transit?.bus_stops || ''),
      ...(points?.transit?.train_stations || '')
    ],
    points?.schools,
    points?.grocery_stores,
    points?.cafes,
    points?.restaurants,
    points?.parks,
    points?.shopping_centers
  ]
  const tabContent = allTabs[currentTab] || []

  const setDefaultCurrentPoint = () => {
    setCurrentPoint({
      name: '',
      address: '',
      distance_walktime: null,
      distance: null,
      latitude: null,
      longitude: null
    })
  }

  useEffect(() => {
    setCurrentPoint({
      name: listing.name,
      address: listing.address,
      distance_walktime: '0',
      distance: '0',
      latitude: listing.lat,
      longitude: listing.lng
    })
  }, [
    currentTab,
    buildingId,
    listing.name,
    listing.address,
    listing.lat,
    listing.lng
  ])

  const variants = {
    expanded: {
      maxHeight: 400,
      transition: { duration: 0.8, type: 'spring', stiffness: 40, damping: 15 }
    },
    collapsed: {
      maxHeight: 50,
      transition: { duration: 0.3, type: 'spring', stiffness: 90, damping: 15 }
    }
  }

  const nearbyNeighbourhoods = () => {
    if (points?.neighbourhoods?.length) {
      const nnText = points.neighbourhoods.map(
        (neighbourhood: any) => ' ' + neighbourhood?.info?.name
      )
      return (
        t('poi:b.listings.poi.rental_near', 'This rental is near ') +
        nnText.join(',')
      )
    } else {
      return t(
        'poi:b.listings.poi.neighbourhoods',
        'There are no nearby neighbourhoods.'
      )
    }
  }

  const nearbySchools = () => {
    if (points?.schools?.length) {
      const nsText = points.schools.map(
        (school: any) => ' ' + school?.info?.name
      )
      return (
        t('poi:b.listings.poi.rental_near', 'This rental is near ') +
        nsText.join(',')
      )
    } else {
      return t('poi:b.listings.poi.schools', 'There are no nearby schools.')
    }
  }

  const schoolsText = nearbySchools()
  const neighbourhoodsText = nearbyNeighbourhoods()

  useEffect(() => {
    if (schoolsText?.length > 150) {
      setShowEllipsis(true)
    }
  }, [schoolsText])

  const buttonTextVariants = {
    hidden: { opacity: 0, duration: 0.0 },
    visible: { opacity: 1 }
  }

  return (
    <Wrapper ref={scope}>
      {!hideContent && (
        <>
          <Row
            style={{
              marginBottom: isMobile ? '22px' : '55px',
              gap: isMobile ? '32px' : '40px'
            }}
          >
            <Section>
              <Title>
                {t(
                  'poi:b.listings.poi.nearby_neighbourhoods',
                  'Nearby Neighbourhoods'
                )}
              </Title>
              <TextWrapper>
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10
                    }}
                  >
                    <Skeleton width={300} height={40} />
                    <Skeleton width={400} height={20} />
                    <Skeleton width={400} height={20} />
                  </div>
                ) : (
                  neighbourhoodsText
                )}
              </TextWrapper>
            </Section>
            <Section>
              <Title>
                {t('poi:b.listings.poi.nearby_schools', 'Nearby Schools')}
              </Title>
              <TextWrapper
                initial={false}
                animate={schoolsReadMore ? 'expanded' : 'collapsed'}
                variants={variants}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10
                    }}
                  >
                    <Skeleton width={300} height={40} />
                    <Skeleton width={400} height={20} />
                    <Skeleton width={400} height={20} />
                  </div>
                ) : showEllipsis ? (
                  schoolsText.slice(0, 135) + '...'
                ) : (
                  schoolsText
                )}
              </TextWrapper>
              {schoolsText?.length > 157 && (
                <motion.button
                  initial="hidden"
                  animate={inAction ? 'hidden' : 'visible'}
                  variants={buttonTextVariants}
                  style={{
                    width: '80px',
                    justifyContent: 'flex-start',
                    padding: '0',
                    marginTop: '-10px',
                    color: colors.link,
                    background: 'none',
                    border: 'none',
                    textAlign: 'left'
                  }}
                  disabled={inAction}
                  onClick={onToggleShowSchools}
                >
                  {schoolsReadMore
                    ? t(
                        'b.components.read_more_text.read_less.text',
                        'Read Less'
                      )
                    : t(
                        'b.components.read_more_text.read_more.text',
                        'Read More'
                      )}
                </motion.button>
              )}
            </Section>
          </Row>
          <Row style={{ marginBottom: '32px' }}>
            <Section style={{ gap: '12px' }}>
              <Title>{t('common.neighbourhood', 'Neighbourhood')}</Title>
              <TextWrapper>
                {t(
                  'poi:b.listings.poi.distance_disclamer',
                  'We only display the points of interest located within a 10 km radius of the listing.'
                )}
              </TextWrapper>
            </Section>
          </Row>
        </>
      )}
      {!isMobile && (
        <TabBar
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          setActiveId={setActiveId}
          hideSchools={hideSchools}
        />
      )}
      <Row style={{ marginTop: '32px' }}>
        <Section>
          {!isLoading && (
            <MapComponent
              currentPoint={currentPoint}
              setCurrentPoint={setCurrentPoint}
              positions={tabContent}
              setDefaultCurrentPoint={setDefaultCurrentPoint}
              setActiveId={setActiveId}
              tabIndex={currentTab}
              listing={listing}
              isHidden={isStreetNumberHidden}
              icon={icon}
              id="poi-map"
              customBuildingColor={customBuildingColor}
              customPoiColor={customPoiColor}
              isListingLocked={isListingLocked}
            />
          )}
        </Section>
        {isMobile && (
          <TabBar
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            setActiveId={setActiveId}
            hideSchools={hideSchools}
          />
        )}
        <Section>
          <PoiList
            points={tabContent}
            setCurrentPoint={setCurrentPoint}
            activeId={activeId}
            setActiveId={setActiveId}
            currentTab={currentTab}
            isLoading={isLoading}
          />
        </Section>
      </Row>
    </Wrapper>
  )
}

export default PointsOfInterest
